<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import { useRouter } from 'vue-router'
import { routes } from '@app/router/routes'
import Drawer from '@uiKit/elements/Drawer.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import {
  MY_LISTING_STATUS,
  resolveTradingError,
  tradingErrorCopy,
  useSellOverviewQuery,
  varianceVsValuation,
} from '../../trading'
import SellDrawerHeader from '../components/SellDrawerHeader.vue'
import SellDrawerFooter from '../components/SellDrawerFooter.vue'
import SellStepper from '../components/SellStepper.vue'
import SellExitOverviewStep from '../components/SellExitOverviewStep.vue'
import SellDividendNoticeStep from '../components/SellDividendNoticeStep.vue'
import SellQuantityStep from '../components/SellQuantityStep.vue'
import SellPriceStep from '../components/SellPriceStep.vue'
import SellReviewStep from '../components/SellReviewStep.vue'
import SellUnderReview from '../components/SellUnderReview.vue'
import SellRejected from '../components/SellRejected.vue'
import { useSellListingDraft, type SellListingSeed } from '../composables/useSellListingDraft'
import { useSellListingQuote } from '../composables/useSellListingQuote'
import { useSellListingSubmit } from '../composables/useSellListingSubmit'
import {
  DEFAULT_REJECTION_REASON,
  SELL_STEPPER_LABELS,
  SELL_STEP_TITLES,
  type SellStep,
} from '../constants/sellCopy'

// Secondary-market sell drawer (spec 1.7.3; Figma 34796:138630 → 138922):
// Exit overview → Rental-income notice (acknowledged) → Quantity → Price →
// Confirm Listing → Under Review (or Rejected). Mounted by portfolio's
// investment detail and by the orders listings (relist / sell more). Every
// figure is the server's: eligibility + exit overview from sell-overview,
// proceeds from sell-quote. Closing throws the draft away.

interface Props {
  open: boolean
  /** PROPERTY id whose tokens are being listed (not the offering / holding id). */
  propertyId: string
  /** Display name — the caller usually knows it before the overview loads. */
  propertyTitle?: string
  /** Relist: start the draft from the prior listing's quantity + price. */
  prefill?: SellListingSeed | null
}

const props = withDefaults(defineProps<Props>(), { propertyTitle: '', prefill: null })
const emit = defineEmits<{ (e: 'close'): void }>()

const router = useRouter()

const propertyId = toRef(props, 'propertyId')
const overviewQuery = useSellOverviewQuery(propertyId)

const overview = computed(() => overviewQuery.data.value ?? null)
const isLoading = computed(() => overviewQuery.isLoading.value)
const hasLoadError = computed(() => overviewQuery.isError.value)
const loadErrorMessage = computed(() =>
  resolveTradingError(
    overviewQuery.error.value,
    "We couldn't load this holding. Close and try again.",
  ),
)
const title = computed(() => props.propertyTitle || overview.value?.propertyTitle || '')

const figures = computed(() => ({
  available: overview.value?.tokensSellable ?? 0,
  valuation: overview.value?.valuation ?? 0,
  bandMin: overview.value?.bandMin ?? 0,
  bandMax: overview.value?.bandMax ?? 0,
  minListingAmount: overview.value?.minListingAmount ?? 0,
  isSellAllRemainingEligible: overview.value?.isSellAllRemainingEligible ?? false,
}))

const ineligibleReasons = computed(() =>
  (overview.value?.ineligibleReasons ?? []).map(
    (code) => tradingErrorCopy(code) ?? 'This holding can’t be listed right now.',
  ),
)
const canSell = computed(() => Boolean(overview.value?.isTradable && overview.value.canSell))

const draft = useSellListingDraft(figures)
const { tokensText, priceText, tokens, price, isOutsideBand, errors } = draft

const { quote, isQuoting, quoteError } = useSellListingQuote({ propertyId, tokens, price, figures })
// Server-reported price problems join the instant checks on the price step.
const priceStepError = computed(() => errors.value.price ?? quoteError.value)
// The server's variance once priced; the same formula meanwhile so the price
// step reacts as the investor types.
const variancePct = computed(() => {
  if (quote.value) return quote.value.variancePct
  return price.value !== null && figures.value.valuation > 0
    ? varianceVsValuation(price.value, figures.value.valuation)
    : null
})
const grossValue = computed(() => quote.value?.grossAmount ?? tokens.value * (price.value ?? 0))

// ── Steps ───────────────────────────────────────────────────────────────
const step = ref<SellStep>('overview')
const isDividendAcknowledged = ref(false)
const submission = useSellListingSubmit()
const { hasAcceptedTerms, listing, isSubmitting } = submission

watch(
  () => props.open,
  () => {
    step.value = 'overview'
    isDividendAcknowledged.value = false
    submission.reset()
    draft.reset(props.prefill)
  },
  { immediate: true },
)

const STEPPER_POSITION: Partial<Record<SellStep, number>> = {
  overview: 1,
  dividend: 1,
  quantity: 2,
  price: 3,
}
const stepperCurrent = computed(() => STEPPER_POSITION[step.value] ?? 1)
const hasStepper = computed(() => step.value in STEPPER_POSITION)

const PREVIOUS_STEP: Partial<Record<SellStep, SellStep>> = {
  dividend: 'overview',
  quantity: 'dividend',
  price: 'quantity',
  review: 'price',
}
const canGoBack = computed(() => Boolean(PREVIOUS_STEP[step.value]))

const goBack = () => {
  submission.error.value = null
  step.value = PREVIOUS_STEP[step.value] ?? 'overview'
}

const continueFromOverview = () => {
  if (canSell.value) step.value = 'dividend'
}

const continueFromDividend = () => {
  if (isDividendAcknowledged.value) step.value = 'quantity'
}

const continueFromQuantity = () => {
  if (!draft.check('quantity')) return
  draft.suggestPrice()
  step.value = 'price'
}

const continueFromPrice = () => {
  if (draft.check('price') && quote.value && !quoteError.value) step.value = 'review'
}

const createListing = async () => {
  if (price.value === null) return
  const isCreated = await submission.submit({
    propertyId: props.propertyId,
    tokens: tokens.value,
    pricePerToken: price.value,
    dividendNoticeAccepted: isDividendAcknowledged.value,
  })
  if (isCreated)
    step.value = listing.value?.status === MY_LISTING_STATUS.REJECTED ? 'rejected' : 'underReview'
}

const close = () => emit('close')

const viewMyListings = () => {
  router.push({ path: routes.MARKETPLACE_ORDERS, query: { tab: 'listings' } })
  close()
}

const backToPortfolio = () => {
  router.push(routes.PORTFOLIO)
  close()
}

const FOOTER_ACTIONS: Record<SellStep, { label: string; run: () => void }> = {
  overview: { label: 'Continue', run: continueFromOverview },
  dividend: { label: 'Continue', run: continueFromDividend },
  quantity: { label: 'Continue', run: continueFromQuantity },
  price: { label: 'Continue', run: continueFromPrice },
  review: { label: 'Create Listing', run: createListing },
  underReview: { label: 'View My Listings', run: viewMyListings },
  rejected: { label: 'Back to Portfolio', run: backToPortfolio },
}
const footerAction = computed(() => FOOTER_ACTIONS[step.value])
const isReady = computed(() => !isLoading.value && !hasLoadError.value)

const isFooterDisabled = computed(() => {
  if (step.value === 'overview') return !canSell.value
  if (step.value === 'dividend') return !isDividendAcknowledged.value
  if (step.value === 'price')
    return isOutsideBand.value || isQuoting.value || Boolean(quoteError.value)
  if (step.value === 'review') return !quote.value
  return false
})
</script>

<template>
  <Drawer
    :model-value="open"
    position="right"
    width="480px"
    :show-close-icon="false"
    :dismissable="!isSubmitting"
    @update:model-value="(visible) => !visible && !isSubmitting && close()"
  >
    <template #header>
      <SellDrawerHeader
        :title="SELL_STEP_TITLES[step]"
        :can-go-back="canGoBack"
        :disabled="isSubmitting"
        @back="goBack"
        @close="close"
      />
    </template>

    <Box direction="col" class="sell-drawer">
      <Box direction="col" :gap="hasStepper ? '6' : '14px'" grow class="sell-drawer__body">
        <Box v-if="isLoading" direction="col" gap="4">
          <Skeleton height="44px" border-radius="12px" />
          <Skeleton height="56px" border-radius="12px" />
          <Skeleton height="190px" border-radius="16px" />
          <Skeleton height="72px" border-radius="12px" />
        </Box>

        <Box v-else-if="hasLoadError" direction="col" align="center" justify="center" gap="3" grow>
          <PiIcon name="exclamation-circle" class="sell-drawer__error-icon" />
          <Text type="body" align="center">{{ loadErrorMessage }}</Text>
        </Box>

        <template v-else-if="overview">
          <SellStepper v-if="hasStepper" :labels="SELL_STEPPER_LABELS" :current="stepperCurrent" />

          <SellExitOverviewStep
            v-if="step === 'overview'"
            :valuation="overview.valuation"
            :valuation-date="overview.valuationDate"
            :average-cost="overview.averageCost"
            :appreciation-per-token="overview.appreciationPerToken"
            :appreciation-pct="overview.appreciationPct"
            :tokens-held="overview.tokensHeld"
            :tokens-locked="overview.tokensLocked"
            :tokens-in-lock-in="overview.tokensInLockIn"
            :lock-in-ends-at="overview.lockInEndsAt"
            :tokens-sellable="overview.tokensSellable"
            :is-tradable="overview.isTradable"
            :ineligible-reasons="ineligibleReasons"
          />

          <SellDividendNoticeStep
            v-else-if="step === 'dividend'"
            v-model="isDividendAcknowledged"
          />

          <SellQuantityStep
            v-else-if="step === 'quantity'"
            v-model:tokens="tokensText"
            :tokens-held="overview.tokensHeld"
            :available="figures.available"
            :min-listing-amount="figures.minListingAmount"
            :error="errors.quantity"
            @blur="tokensText && draft.check('quantity')"
          />

          <SellPriceStep
            v-else-if="step === 'price'"
            v-model:price-text="priceText"
            :valuation="figures.valuation"
            :band-min="figures.bandMin"
            :band-max="figures.bandMax"
            :band-pct="overview.bandPct"
            :price="price"
            :tokens="tokens"
            :variance-pct="variancePct"
            :gross-value="grossValue"
            :is-outside-band="isOutsideBand"
            :error="priceStepError"
            @blur="priceText && draft.check('price')"
          />

          <SellReviewStep
            v-else-if="step === 'review' && price !== null"
            :property-title="title"
            :tokens="tokens"
            :price="price"
            :average-cost="overview.averageCost"
            :variance-pct="variancePct"
            :quote="quote"
          />

          <SellUnderReview
            v-else-if="step === 'underReview' && listing"
            :reference="listing.reference"
            :tokens="listing.tokensListed"
            :price-per-token="listing.pricePerToken"
          />

          <SellRejected
            v-else-if="step === 'rejected' && listing"
            :reason="listing.rejectionReason || DEFAULT_REJECTION_REASON"
            :tokens="listing.tokensListed"
            :valuation="figures.valuation"
            :band-min="figures.bandMin"
            :band-max="figures.bandMax"
          />
        </template>
      </Box>

      <SellDrawerFooter
        v-if="isReady"
        v-model:terms="hasAcceptedTerms"
        :label="footerAction.label"
        :has-terms="step === 'review'"
        :error="step === 'review' ? submission.error.value : null"
        :loading="step === 'review' && isSubmitting"
        :disabled="isFooterDisabled"
        @submit="footerAction.run"
      />
    </Box>
  </Drawer>
</template>

<style scoped>
.sell-drawer {
  min-height: 100%;
  background: var(--p-sliceal-page-background);
}

.sell-drawer__body {
  min-width: 0;
  padding: 20px;
}

@media (max-width: 992px) {
  .sell-drawer__body {
    padding: 1rem;
  }
}

.sell-drawer__error-icon {
  font-size: 28px;
  color: var(--p-danger-500);
}
</style>
